import { TStoryElement } from 'context/StudioEditorProvider';

const PLACEHOLDER_RE = /\{\s*([a-zA-Z][\w]*)\s*(?:\|([^}]*))?\}/g;

const fromText = (text: string | undefined, into: Set<string>): void => {
  if (!text) {
    return;
  }
  const matches = text.matchAll(PLACEHOLDER_RE);
  Array.from(matches).forEach((match) => into.add(match[1].toLowerCase()));
};

export const substituteText = (text: string, attrs: Record<string, string>): string =>
  text.replace(PLACEHOLDER_RE, (_match, rawKey: string, fallback?: string) => {
    const key = rawKey.toLowerCase();
    const value = attrs[key];
    if (value !== undefined && value !== '') {
      return value;
    }
    return fallback ?? rawKey;
  });

export const substituteElement = <T extends TStoryElement>(
  element: T,
  attrs: Record<string, string>
): T => {
  const sub = (value?: string) => (value === undefined ? value : substituteText(value, attrs));
  return {
    ...element,
    content: sub(element.content),
    question: sub(element.question),
    options: element.options?.map((option) => substituteText(option, attrs)),
    reveal: sub(element.reveal),
    endedText: sub(element.endedText),
  };
};

export const collectFramePlaceholders = (frames: { elements: TStoryElement[] }[]): string[] => {
  const keys = new Set<string>();
  frames.forEach((frame) => {
    frame.elements.forEach((element) => {
      fromText(element.content, keys);
      fromText(element.question, keys);
      fromText(element.reveal, keys);
      fromText(element.endedText, keys);
      (element.options ?? []).forEach((option) => fromText(option, keys));
    });
  });
  return Array.from(keys).sort();
};

/**
 * Placeholders the given attribute list cannot satisfy.
 *
 * Case-insensitive on purpose: `collectFramePlaceholders` lowercases every key it collects, while
 * a segment's `attributeKeys` keep whatever case the Metabase model used — so a raw `includes`
 * reports `firstName` as missing for `{firstname}`.
 *
 * An empty `attributeKeys` means nothing is available, so every placeholder comes back missing.
 * That is the "audience cleared" case, not a special case.
 */
export const missingAttributeKeys = (placeholders: string[], attributeKeys: string[]): string[] => {
  const available = new Set(attributeKeys.map((key) => key.toLowerCase()));
  return placeholders.filter((key) => !available.has(key.toLowerCase()));
};
